import { AppValidator } from "@/common/app-validate.ts";
import { MANAGER_PATH } from "@/constant/url-path.ts";
import { toast } from "@/utils/toast.tsx";
import { Button, Col, Form, Input, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
// Removed UploadImageButton as thumbnail input is no longer shown
import ManageContentLayout from "@/component/common/layout/ManageContentLayout.tsx";
import CustomRequiredMarkAntdForm from "@/component/CustomRequiredMarkAntdForm.tsx";
import { REGEX } from "@/constant/constants.ts";
import { ProductService } from "@/services/product/ProductService.ts";

const AddProductPage = ({ isEdit = false }: { isEdit?: boolean }) => {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const validators = AppValidator(t);

  const [productInfoForm] = Form.useForm<CreateProductRequest>();
  const queryParams = new URLSearchParams(location.search);
  const productCode = queryParams.get("productCode") || null;

  useEffect(() => {
    async function getDetailUser() {
      if (productCode) {
        const res: ProductDetail | null | undefined =
          await ProductService.getDetail(productCode);
        if (res) {
          const mapped = {
            ...res,
            productName: res.productName ?? res.name ?? "",
          } as ProductDetail;
          productInfoForm.setFieldsValue(mapped);
        }
      }
    }

    if (isEdit) {
      getDetailUser().then();
    }
  }, [isEdit, productCode, productInfoForm]);

  const onSubmit = (values: any) => {
    if (isEdit) {
      onEdit(values).then();
    } else {
      onSave(values).then();
    }
  };

  const buildRequestData = (values: CreateProductRequest) => {
    return { ...values };
  };

  const onSave = async (values: CreateProductRequest) => {
    const request: CreateProductRequest = buildRequestData(values);
    const res: SuccessResponse | null | undefined =
      await ProductService.create(request);
    if (res) {
      navigation(MANAGER_PATH.PRODUCT.LIST);
      toast.success(res?.message || "Success");
    }
  };

  const onEdit = async (values: CreateProductRequest) => {
    if (productCode) {
      const formData: CreateProductRequest = buildRequestData(values);
      const res: SuccessResponse | null | undefined =
        await ProductService.update(productCode, formData);
      if (res) {
        navigation(MANAGER_PATH.PRODUCT.LIST);
        toast.success(res?.message || "Success");
      }
    }
  };

  const onCancel = () => {
    navigation(MANAGER_PATH.PRODUCT.LIST);
  };

  return (
    <>
      <ManageContentLayout headerFunction={<></>}>
        <div
          className={"p-5 rounded-lg border bg-white"}
          style={{ minHeight: "753px" }}
        >
          <Form
            form={productInfoForm}
            name="product-info-form"
            layout={"vertical"}
            colon={false}
            requiredMark={CustomRequiredMarkAntdForm}
            onFinish={onSubmit}
            size={"large"}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item<CreateProductRequest>
                  name="productName"
                  label={t("field.productName")}
                  rules={[validators.required(t("field.productName"))]}
                >
                  <Input
                    max={255}
                    disabled={isEdit}
                    placeholder={t("placeholder.enterProductName")}
                  />
                </Form.Item>
                {/* productCode field removed (non-editable) */}
              </Col>
              <Col span={12}>
                <Form.Item<CreateProductRequest>
                  name="version"
                  label={t("field.version")}
                  rules={[
                    validators.required(t("field.version")),
                    validators.validateFormatWithRegex(
                      REGEX.VERSION,
                      "",
                      t("validation.version"),
                    ),
                  ]}
                >
                  <Input
                    disabled={isEdit}
                    placeholder={t("placeholder.productVersion")}
                  />
                </Form.Item>
                {/* thumbnail input removed */}
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item<CreateProductRequest>
                  name="description"
                  label={t("field.productDescription")}
                  rules={[validators.required(t("field.productDescription"))]}
                >
                  <Input.TextArea
                    rows={10}
                    placeholder={t("placeholder.enterProductDescription")}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item className={"text-end mb-1 mt-5"}>
              <Button type="default" className="m-1" onClick={() => onCancel()}>
                {t("button.cancel")}
              </Button>
              <Button type="primary" htmlType="submit" className="m-1">
                {isEdit ? t("button.save") : t("button.add")}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </ManageContentLayout>
    </>
  );
};

export default AddProductPage;
